import {
    supportsAmelipro,
    supportsAmeliproCpsAuthentication,
    supportsAmeliproProgress
} from '@/core/services/shellCapabilities';
import {
    AdacQueryResult,
    AmeliproCpsAuthentication,
    AmeliproSignInOptions,
    AmeliproStatus,
    AmeliproTraceEntry
} from '@/amelipro/interfaces';

const OFFLINE: AmeliproStatus = {authenticated: false, needsAuthentication: true};

/**
 * Drive the amelipro session that lives in the Electron shell.
 */
const useAmelipro = () => {
    /**
     * Whether this shell can hold an amelipro session at all.
     */
    const isAvailable = (): boolean => supportsAmelipro();

    /**
     * Open the session.
     */
    const connect = async (): Promise<AmeliproStatus> => {
        if (!supportsAmelipro()) {
            return OFFLINE;
        }

        return await window.ipcRenderer.ameliproConnect() as AmeliproStatus;
    };

    /**
     * Sign in with the CPS card, letting the shell click the card entry of the
     * identity provider: the middleware then reads the reader, asks which
     * certificate to use and prompts for the code porteur on its own.
     *
     * Shells that cannot click fall back on the window they know how to
     * reveal, and report the sign-in as a manual one.
     *
     * @param options  Who is there to see the sign-in: nothing from a screen
     *                 the pharmacist is at, `background` otherwise.
     */
    const authenticateWithCps = async (options: AmeliproSignInOptions = {}): Promise<AmeliproCpsAuthentication> => {
        if (supportsAmeliproCpsAuthentication()) {
            return await window.ipcRenderer.ameliproAuthenticateCps(options) as AmeliproCpsAuthentication;
        }

        if (!supportsAmelipro()) {
            return {authenticated: false, step: 'timeout'};
        }

        const session = await connect();

        return {authenticated: session.authenticated, step: 'manual'};
    };

    /**
     * Read the current session status without triggering authentication.
     */
    const status = async (): Promise<AmeliproStatus> => {
        if (!supportsAmelipro()) {
            return OFFLINE;
        }

        return await window.ipcRenderer.ameliproStatus() as AmeliproStatus;
    };

    /**
     * Query ADAC for one patient and one product category.
     *
     * The failure is handed back rather than flattened to nothing: the reason
     * the portal refused — a session that lapsed, a code the card would not
     * take, a category that answered 401 — is the whole of what the screen has
     * to tell the pharmacist, and there is no second place to read it from.
     *
     * @param nir   The patient NIR.
     * @param slug  The ADAC category slug.
     * @returns The verdict, or why there is none.
     */
    const queryAdac = async (nir: string, slug: string): Promise<AdacQueryResult> => {
        if (!supportsAmelipro()) {
            return {error: 'amelipro:unsupported'};
        }

        return await window.ipcRenderer.ameliproQueryAdac(nir, slug) as AdacQueryResult;
    };

    /**
     * TEMPORARY — follow the shell's amelipro cycle step by step, for the ADAC
     * debugging aid. Shells that cannot push steps are listened to for nothing.
     *
     * @param callback Receives each step as the shell writes it down.
     * @returns The function that stops listening.
     */
    const onProgress = (callback: (entry: AmeliproTraceEntry) => void): (() => void) => {
        if (!supportsAmeliproProgress()) {
            return () => undefined;
        }

        return window.ipcRenderer.onAmeliproProgress!((entry) => callback(entry as AmeliproTraceEntry));
    };

    return {
        isAvailable,
        connect,
        authenticateWithCps,
        status,
        queryAdac,
        onProgress,
    };
};

export default useAmelipro;
